import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { fmtCupo } from '../../utils/format';
import { Building2, Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, CalendarPlus, X, Check, AlertCircle, CheckCircle2, Users, UserCheck, GitBranch, Briefcase, Plus, Pencil, Trash2, KeyRound, UserPlus } from 'lucide-react';
import { formatDDMMYYYY } from '../../utils/date';
import { ModalEmpresa, ModalUsuarioEse, ModalResetClave, ModalEliminarEmpresa } from './CapacitadorUsuariosEmpresa';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PER_PAGE = 8;

const toTitleCase = (s) => {
  if (!s) return '';
  return s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase());
};

const formatRut = (rut) => {
  if (!rut) return '';
  const clean = rut.replace(/[.\-]/g, '');
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  const formatted = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${formatted}-${dv.toUpperCase()}`;
};

// Valida el dígito verificador del RUT chileno (Módulo 11)
const validateRut = (rut) => {
  const clean = (rut || '').replace(/[.\-\s]/g, '').toUpperCase();
  if (clean.length < 2) return false;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  if (!/^\d+$/.test(body)) return false;
  let sum = 0, mult = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i], 10) * mult;
    mult = mult === 7 ? 2 : mult + 1;
  }
  const r = sum % 11;
  const calc = r === 0 ? '0' : r === 1 ? 'K' : String(11 - r);
  return dv === calc;
};

// Formatea a medida que escribe (RUT chileno con puntos y guión)
const formatRutLive = (s) => {
  const c = (s || '').replace(/[^0-9kK]/g, '').toUpperCase();
  if (!c) return '';
  if (c.length === 1) return c;
  const body = c.slice(0, -1);
  const dv = c.slice(-1);
  return `${body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv}`;
};

const isRutSearch = (q) => /^\d{2,}/.test(q.replace(/[.\-]/g, ''));

export default function CapacitadorEmpresas() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [empresas, setEmpresas] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAsignar, setShowAsignar] = useState(null);
  const [sesiones, setSesiones] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [workerCaps, setWorkerCaps] = useState([]);
  const [rutResults, setRutResults] = useState(null);
  const [stats, setStats] = useState(null);
  const [esesByEmpresa, setEsesByEmpresa] = useState({}); // { empresaId: [users] }
  const [tabExp, setTabExp] = useState('trabajadores'); // 'trabajadores' | 'ese'
  const [modalEmpresa, setModalEmpresa] = useState(false);
  const [modalDelEmpresa, setModalDelEmpresa] = useState(null);
  const [modalUserEse, setModalUserEse] = useState(null); // { mode, user?, empresaId? }
  const [modalReset, setModalReset] = useState(null);
  const [modalTrab, setModalTrab] = useState(null); // { mode, worker?, empresaId }
  const [modalDelTrab, setModalDelTrab] = useState(null); // { worker, empresaId }
  const [trabSearch, setTrabSearch] = useState(''); // Filtro de trabajadores dentro de la empresa expandida
  const searchTimeout = useRef(null);

  const fetchEmpresas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/empresas-colaboradoras`, { headers });
      setEmpresas(res.data || []);
    } catch { }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/empresas-colaboradoras/stats`, { headers });
      setStats(res.data);
    } catch { }
  }, [token]);

  const fetchEseReport = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/capacitador/usuarios-por-empresa`, { headers });
      const map = {};
      (r.data?.empresas || []).forEach(e => { map[e.empresa_id] = e.usuarios || []; });
      setEsesByEmpresa(map);
    } catch { }
  }, [token]);

  const fetchSesiones = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sesiones`, { headers });
      // Ordenar por fecha DESC (más reciente arriba). Empatar por hora_inicio DESC.
      const sorted = [...(res.data || [])].sort((a, b) => {
        const fa = a.fecha || '';
        const fb = b.fecha || '';
        if (fa !== fb) return fb.localeCompare(fa);
        return (b.hora_inicio || '').localeCompare(a.hora_inicio || '');
      });
      setSesiones(sorted);
    } catch { }
  }, [token]);

  useEffect(() => { fetchEmpresas(); fetchSesiones(); fetchStats(); fetchEseReport(); }, [fetchEmpresas, fetchSesiones, fetchStats, fetchEseReport]);

  const searchByRut = useCallback(async (q) => {
    if (!isRutSearch(q) || q.replace(/[.\-]/g, '').length < 3) {
      setRutResults(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/empresas-colaboradoras/buscar-trabajador?q=${encodeURIComponent(q)}`, { headers });
      setRutResults(res.data);
    } catch { setRutResults(null); }
  }, [token]);

  useEffect(() => {
    setPage(1);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchByRut(search), 300);
    return () => clearTimeout(searchTimeout.current);
  }, [search, searchByRut]);

  const toggleExpand = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    setTabExp('trabajadores');
    setTrabSearch('');
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/empresas-colaboradoras/${id}/usuarios`, { headers });
      setUsuarios(res.data);
    } catch { setUsuarios([]); }
    setLoadingUsers(false);
  };

  const reloadExpanded = async (id) => {
    if (!id) return;
    try {
      const res = await axios.get(`${API}/empresas-colaboradoras/${id}/usuarios`, { headers });
      setUsuarios(res.data);
      fetchEseReport();
    } catch { }
  };

  const openAsignar = async (worker, empresaId) => {
    setShowAsignar({ worker, empresaId });
    setWorkerCaps([]);
    try {
      const res = await axios.get(`${API}/trabajador/${worker.rut}/capacitaciones`, { headers });
      setWorkerCaps(res.data);
    } catch { }
  };

  const handleAsignar = async (sesion, worker, empresaId) => {
    setAssigning(true);
    try {
      await axios.post(`${API}/sesiones/inscribir`, {
        sesion_id: sesion.id,
        empresa_id: empresaId,
        trabajadores: [{ rut: worker.rut, nombre: worker.nombre }]
      }, { headers });
      toast.success(`${toTitleCase(worker.nombre)} inscrito(a) en ${toTitleCase(sesion.capacitacion_nombre)}`);
      setShowAsignar(null);
      fetchSesiones();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al inscribir');
    }
    setAssigning(false);
  };

  const filtered = empresas.filter(e => {
    const q = search.toLowerCase();
    if (!q) return true;
    if (rutResults !== null) return false;
    return e.razon_social?.toLowerCase().includes(q) || e.rut?.includes(q) || (e.gerencia_nombre || '').toLowerCase().includes(q);
  });

  const showRutMode = rutResults !== null && rutResults.length > 0;
  const displayList = showRutMode ? [] : filtered;
  const totalPages = Math.ceil(displayList.length / PER_PAGE);
  const paginated = displayList.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const renderWorkerRow = (u, i, empresaId) => {
    const sexo = (u.sexo || '').toUpperCase();
    const generoBadge = sexo === 'MASCULINO'
      ? <span className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-bold border border-blue-100">M</span>
      : sexo === 'FEMENINO'
      ? <span className="inline-block px-2 py-0.5 rounded-full bg-pink-50 text-pink-700 text-[10px] font-bold border border-pink-100">F</span>
      : <span className="text-slate-300 text-xs">-</span>;
    return (
      <tr key={u.id || i} className="border-b border-slate-50 hover:bg-slate-50/50">
        <td className="px-3 py-2 text-slate-400">{i + 1}</td>
        <td className="px-3 py-2 font-mono text-xs">{formatRut(u.rut)}</td>
        <td className="px-3 py-2">{toTitleCase(`${u.nombre || ''} ${u.apellido || ''}`.trim())}</td>
        <td className="px-3 py-2 text-slate-500 text-xs">{toTitleCase(u.cargo || '')}</td>
        <td className="px-3 py-2">{generoBadge}</td>
        <td className="px-3 py-2 text-slate-500 text-xs">{(u.email || '-').toLowerCase()}</td>
        <td className="px-3 py-2 text-right">
          <div className="inline-flex items-center gap-1">
            <button onClick={() => openAsignar(u, empresaId)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-teal-600 text-white rounded-lg text-xs hover:bg-teal-700 transition-colors"
              data-testid={`asignar-btn-${u.rut}`} title="Asignar a sesión">
              <CalendarPlus className="w-3 h-3" />Asignar
            </button>
            <button onClick={() => setModalTrab({ mode: 'edit', worker: u, empresaId })}
              className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded" title="Editar" data-testid={`trab-editar-${u.rut}`}>
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setModalDelTrab({ worker: u, empresaId })}
              className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded" title="Eliminar" data-testid={`trab-eliminar-${u.rut}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const renderEseRow = (u, i) => (
    <tr key={u.id || i} className="border-b border-slate-50 hover:bg-slate-50/50">
      <td className="px-3 py-2 text-slate-400">{i + 1}</td>
      <td className="px-3 py-2 font-mono text-xs">{u.rut ? formatRut(u.rut) : '—'}</td>
      <td className="px-3 py-2">{toTitleCase(`${u.nombre || ''} ${u.apellido || ''}`.trim())}</td>
      <td className="px-3 py-2 text-slate-500 text-xs">{toTitleCase(u.cargo || '')}</td>
      <td className="px-3 py-2 text-slate-500 text-xs">{(u.email || '-').toLowerCase()}</td>
      <td className="px-3 py-2 text-right">
        <div className="inline-flex items-center gap-1">
          <button onClick={() => setModalUserEse({ mode: 'edit', user: u, empresaId: expanded })}
            className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded" title="Editar" data-testid={`ese-editar-${u.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setModalReset({ userId: u.id, nombre: u.nombre })}
            className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded" title="Resetear clave" data-testid={`ese-reset-${u.id}`}>
            <KeyRound className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => handleDeleteEse(u)}
            className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded" title="Eliminar" data-testid={`ese-eliminar-${u.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );

  const handleDeleteEse = async (u) => {
    if (!window.confirm(`¿Eliminar al usuario ESE "${u.nombre}"? Esto también eliminará al trabajador vinculado.`)) return;
    try {
      await axios.delete(`${API}/capacitador/ese-usuarios/${u.id}`, { headers });
      toast.success('Usuario ESE eliminado.');
      reloadExpanded(expanded);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleDeleteTrabajador = async () => {
    if (!modalDelTrab) return;
    try {
      await axios.delete(`${API}/capacitador/empresas/${modalDelTrab.empresaId}/trabajadores/${modalDelTrab.worker.id}`, { headers });
      toast.success('Trabajador eliminado.');
      setModalDelTrab(null);
      reloadExpanded(modalDelTrab.empresaId);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-6" data-testid="capacitador-empresas">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Colaboradores</h1>
          <p className="text-xs text-slate-500 mt-0.5">Gestiona empresas, usuarios ESE y trabajadores desde un solo lugar.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400 mr-2">{showRutMode ? `${rutResults.length} resultado(s)` : `${filtered.length} empresas`}</span>
          <button onClick={() => setModalEmpresa(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-semibold rounded-lg transition-colors"
            data-testid="btn-nueva-empresa">
            <Plus className="w-4 h-4" />Nueva Empresa
          </button>
          <button onClick={() => setModalUserEse({ mode: 'create' })}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg transition-colors"
            data-testid="btn-nuevo-usuario-ese">
            <UserPlus className="w-4 h-4" />Nuevo Usuario ESE
          </button>
        </div>
      </div>

      {/* KPI cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="colab-kpis">
          <KpiCard icon={Building2} label="Empresas" value={stats.total_empresas}
            subtitle={`${stats.empresas_con_trabajadores} con trabajadores`}
            color="#0F766E" testid="kpi-empresas" />
          <KpiCard icon={Users} label="Trabajadores" value={stats.total_trabajadores.toLocaleString('es-CL')}
            subtitle={`${stats.total_vinculos.toLocaleString('es-CL')} vínculos empresa-trabajador`}
            color="#1D4ED8" testid="kpi-trabajadores" />
          <KpiCard icon={GitBranch} label="Promedio x Empresa" value={stats.promedio_trabajadores_por_empresa}
            subtitle="Trabajadores promedio por ESE"
            color="#D97706" testid="kpi-promedio" />
          <KpiCard icon={UserCheck} label="Usuarios ESE" value={stats.usuarios_ese}
            subtitle="Cuentas de login rol ESE"
            color="#7C3AED" testid="kpi-usuarios-ese" />
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por empresa, gerencia o RUT de trabajador..."
          className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-white" data-testid="search-empresa" />
      </div>

      {/* Resultados por RUT de trabajador */}
      {showRutMode && (
        <div className="space-y-2">
          {rutResults.map((r, i) => (
            <div key={`${r.rut}-${r.empresa_id}`} className="bg-white rounded-xl border border-indigo-200 p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                      <Search className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">{toTitleCase(r.nombre)}</h3>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        <span className="text-xs text-slate-400 font-mono">{formatRut(r.rut)}</span>
                        {r.cargo && <span className="text-xs text-slate-600">{toTitleCase(r.cargo)}</span>}
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-medium">{toTitleCase(r.empresa_nombre)}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{(r.email || '').toLowerCase()}</p>
                    </div>
                  </div>
                </div>
                <button onClick={() => openAsignar(r, r.empresa_id)}
                  className="inline-flex items-center gap-1 px-3 py-2 bg-teal-600 text-white rounded-lg text-xs hover:bg-teal-700 transition-colors"
                  data-testid={`asignar-rut-btn-${r.rut}`}>
                  <CalendarPlus className="w-3 h-3" />Asignar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {rutResults !== null && rutResults.length === 0 && search && (
        <div className="text-center py-12 text-slate-400">
          <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No se encontraron trabajadores con ese RUT</p>
        </div>
      )}

      {/* Vista normal por empresas */}
      {!showRutMode && (
        <div className="space-y-2">
          {paginated.map(e => (
            <div key={e.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div onClick={() => toggleExpand(e.id)}
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium text-slate-800 truncate">{toTitleCase(e.razon_social)}</h3>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-slate-400 font-mono">{formatRut(e.rut)}</span>
                      {e.gerencia_nombre && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-medium truncate max-w-[200px]">
                          {toTitleCase(e.gerencia_nombre)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {expanded === e.id ? <ChevronUp className="w-5 h-5 text-slate-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />}
              </div>
              {expanded === e.id && (
                <div className="border-t border-slate-100 px-4 py-3">
                  {/* Tabs */}
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="inline-flex rounded-lg bg-slate-100 p-1">
                      <button onClick={() => setTabExp('trabajadores')}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${tabExp === 'trabajadores' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        data-testid={`tab-trabajadores-${e.id}`}>
                        Trabajadores ({usuarios.length})
                      </button>
                      <button onClick={() => setTabExp('ese')}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${tabExp === 'ese' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        data-testid={`tab-ese-${e.id}`}>
                        Usuarios ESE ({(esesByEmpresa[e.id] || []).length})
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      {tabExp === 'trabajadores' && (
                        <button onClick={() => setModalTrab({ mode: 'create', empresaId: e.id })}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-md"
                          data-testid={`btn-nuevo-trab-${e.id}`}>
                          <Plus className="w-3.5 h-3.5" />Nuevo trabajador
                        </button>
                      )}
                      {tabExp === 'ese' && (
                        <button onClick={() => setModalUserEse({ mode: 'create', empresaId: e.id })}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-md"
                          data-testid={`btn-nuevo-ese-${e.id}`}>
                          <UserPlus className="w-3.5 h-3.5" />Nuevo usuario ESE
                        </button>
                      )}
                      <button onClick={() => setModalDelEmpresa({ id: e.id, razon_social: e.razon_social })}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold rounded-md"
                        data-testid={`btn-eliminar-empresa-${e.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />Eliminar empresa
                      </button>
                    </div>
                  </div>

                  {/* Trabajadores tab */}
                  {tabExp === 'trabajadores' && (
                    (() => {
                      const q = trabSearch.trim().toLowerCase();
                      const qClean = q.replace(/[.\-\s]/g, '');
                      const filtered = q ? usuarios.filter(u => {
                        const nombre = `${u.nombre || ''} ${u.apellido || ''}`.toLowerCase();
                        const rut = (u.rut || '').toLowerCase();
                        const rutClean = rut.replace(/[.\-\s]/g, '');
                        const cargo = (u.cargo || '').toLowerCase();
                        const email = (u.email || '').toLowerCase();
                        return nombre.includes(q) || rut.includes(q) || rutClean.includes(qClean) || cargo.includes(q) || email.includes(q);
                      }) : usuarios;
                      return loadingUsers ? (
                        <p className="text-sm text-slate-400 text-center py-4">Cargando trabajadores...</p>
                      ) : usuarios.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">No hay trabajadores registrados. Usa &quot;Nuevo trabajador&quot; para agregar el primero.</p>
                      ) : (
                        <>
                          <div className="relative mb-3">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input value={trabSearch} onChange={ev => setTrabSearch(ev.target.value)}
                              placeholder="Buscar por nombre, RUT, cargo o email..."
                              className="w-full pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-teal-400"
                              data-testid={`trab-search-${e.id}`} />
                            {trabSearch && (
                              <button type="button" onClick={() => setTrabSearch('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                data-testid={`trab-search-clear-${e.id}`}>
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          {filtered.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-4">Sin resultados para &quot;{trabSearch}&quot;.</p>
                          ) : (
                            <>
                              {q && (
                                <p className="text-xs text-slate-500 mb-2" data-testid={`trab-count-${e.id}`}>
                                  {filtered.length} de {usuarios.length} trabajadores
                                </p>
                              )}
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-slate-200">
                                    <th className="text-left px-3 py-2 text-slate-500 font-medium">#</th>
                                    <th className="text-left px-3 py-2 text-slate-500 font-medium">RUT</th>
                                    <th className="text-left px-3 py-2 text-slate-500 font-medium">Nombre</th>
                                    <th className="text-left px-3 py-2 text-slate-500 font-medium">Cargo</th>
                                    <th className="text-left px-3 py-2 text-slate-500 font-medium">Género</th>
                                    <th className="text-left px-3 py-2 text-slate-500 font-medium">Email</th>
                                    <th className="text-right px-3 py-2 text-slate-500 font-medium">Acciones</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filtered.map((u, i) => renderWorkerRow(u, i, e.id))}
                                </tbody>
                              </table>
                            </>
                          )}
                        </>
                      );
                    })()
                  )}

                  {/* Usuarios ESE tab */}
                  {tabExp === 'ese' && (
                    (esesByEmpresa[e.id] || []).length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">Esta empresa no tiene usuario ESE. Usa &quot;Nuevo usuario ESE&quot; para crear la cuenta de login.</p>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left px-3 py-2 text-slate-500 font-medium">#</th>
                            <th className="text-left px-3 py-2 text-slate-500 font-medium">RUT</th>
                            <th className="text-left px-3 py-2 text-slate-500 font-medium">Nombre</th>
                            <th className="text-left px-3 py-2 text-slate-500 font-medium">Cargo</th>
                            <th className="text-left px-3 py-2 text-slate-500 font-medium">Email</th>
                            <th className="text-right px-3 py-2 text-slate-500 font-medium">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(esesByEmpresa[e.id] || []).map((u, i) => renderEseRow(u, i))}
                        </tbody>
                      </table>
                    )
                  )}
                </div>
              )}
            </div>
          ))}
          {paginated.length === 0 && !showRutMode && (
            <div className="text-center py-12 text-slate-400">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No se encontraron empresas</p>
            </div>
          )}
        </div>
      )}

      {/* Paginacion */}
      {totalPages > 1 && !showRutMode && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-4 py-3">
          <span className="text-sm text-slate-500">
            {(page - 1) * PER_PAGE + 1}-{Math.min(page * PER_PAGE, displayList.length)} de {displayList.length}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let p;
              if (totalPages <= 7) p = i + 1;
              else if (page <= 4) p = i + 1;
              else if (page >= totalPages - 3) p = totalPages - 6 + i;
              else p = page - 3 + i;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium ${p === page ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Modal Asignar a Sesion */}
      {showAsignar && createPortal(
        <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowAsignar(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={ev => ev.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Asignar a Sesion</h2>
                <p className="text-sm text-slate-500">{toTitleCase(showAsignar.worker.nombre)} - {formatRut(showAsignar.worker.rut)}</p>
              </div>
              <button onClick={() => setShowAsignar(null)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {sesiones.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No hay sesiones disponibles</p>
              ) : (
                <div className="space-y-2">
                  {sesiones.map(s => {
                    const full = s.cupo_disponible <= 0;
                    const yaTiene = workerCaps.includes(s.capacitacion_id);
                    return (
                      <div key={s.id} className={`p-3 rounded-lg border transition-colors ${full ? 'border-amber-200 bg-amber-50/50' : yaTiene ? 'border-green-200 bg-green-50/30' : 'border-slate-200 hover:border-teal-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-800 text-sm truncate">{toTitleCase(s.capacitacion_nombre)}</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {formatDDMMYYYY(s.fecha)} | {s.hora_inicio}-{s.hora_fin} | {s.lugar}
                            </p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${full ? 'bg-red-100 text-red-700' : 'bg-teal-100 text-teal-700'}`}>
                                {s.inscritos}/{fmtCupo(s.cupo_maximo)} inscritos
                              </span>
                              {full && <span className="text-[10px] text-amber-600 font-medium">Sobrecupo</span>}
                              {yaTiene && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] text-green-700 font-medium">
                                  <CheckCircle2 className="w-3 h-3" />Ya inscrito en esta capacitacion
                                </span>
                              )}
                            </div>
                          </div>
                          <button onClick={() => handleAsignar(s, showAsignar.worker, showAsignar.empresaId)}
                            disabled={assigning}
                            className="ml-3 shrink-0 inline-flex items-center gap-1 px-3 py-2 bg-teal-600 text-white rounded-lg text-xs hover:bg-teal-700 disabled:opacity-50 transition-colors">
                            <Check className="w-3 h-3" />Asignar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        , document.body)}

      {/* Modales de gestión */}
      {modalEmpresa && (
        <ModalEmpresa
          onClose={() => setModalEmpresa(false)}
          onCreated={() => { setModalEmpresa(false); fetchEmpresas(); fetchStats(); toast.success('Empresa creada.'); }}
          headers={headers}
        />
      )}
      {modalUserEse && (
        <ModalUsuarioEse
          modal={modalUserEse}
          onClose={() => setModalUserEse(null)}
          onSaved={() => { setModalUserEse(null); fetchEseReport(); toast.success('Usuario ESE guardado.'); }}
          headers={headers}
          empresasFromReport={empresas.map(e => ({ empresa_id: e.id, razon_social: e.razon_social }))}
        />
      )}
      {modalReset && (
        <ModalResetClave
          info={modalReset}
          onClose={() => setModalReset(null)}
          onDone={() => setModalReset(null)}
          headers={headers}
        />
      )}
      {modalDelEmpresa && (
        <ModalEliminarEmpresa
          info={modalDelEmpresa}
          onClose={() => setModalDelEmpresa(null)}
          onDeleted={() => { setModalDelEmpresa(null); fetchEmpresas(); fetchStats(); fetchEseReport(); toast.success('Empresa eliminada.'); }}
          headers={headers}
        />
      )}
      {modalTrab && (
        <ModalTrabajador
          info={modalTrab}
          onClose={() => setModalTrab(null)}
          onSaved={() => { const eid = modalTrab.empresaId; setModalTrab(null); reloadExpanded(eid); fetchStats(); }}
          headers={headers}
        />
      )}
      {modalDelTrab && createPortal(
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setModalDelTrab(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="modal-del-trab">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-full bg-red-50"><Trash2 className="w-5 h-5 text-red-600" /></div>
              <h3 className="text-lg font-bold text-slate-900">Eliminar trabajador</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              ¿Confirmas eliminar a <strong>{toTitleCase(`${modalDelTrab.worker.nombre} ${modalDelTrab.worker.apellido || ''}`.trim())}</strong> de esta empresa?
              Si el trabajador no está en otras empresas, se eliminará por completo.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setModalDelTrab(null)}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">Cancelar</button>
              <button onClick={handleDeleteTrabajador}
                className="px-3 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold"
                data-testid="confirm-del-trab">Eliminar</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}

/** Modal simple para crear / editar trabajador (scope Capacitador) */
function ModalTrabajador({ info, onClose, onSaved, headers }) {
  const isEdit = info.mode === 'edit';
  const w = info.worker;
  const [form, setForm] = React.useState({
    rut: w?.rut || '',
    nombre: w?.nombre || '',
    apellido: w?.apellido || '',
    email: w?.email || '',
    fono: w?.fono || '',
    cargo: w?.cargo || '',
    sexo: (w?.sexo || '').toUpperCase(),
    fecha_nacimiento: w?.fecha_nacimiento || '',
  });
  const [saving, setSaving] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rut.trim() || !form.nombre.trim()) {
      toast.error('RUT y Nombre son obligatorios.');
      return;
    }
    if (!form.apellido.trim()) {
      toast.error('El apellido es obligatorio.');
      return;
    }
    const email = (form.email || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('El correo electrónico es obligatorio y debe ser válido.');
      return;
    }
    if (!validateRut(form.rut)) {
      toast.error('El RUT ingresado no es válido.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, rut: formatRut(form.rut) };
      if (isEdit) {
        await axios.put(`${API}/capacitador/trabajadores/${w.id}`, payload, { headers });
        toast.success('Trabajador actualizado.');
      } else {
        await axios.post(`${API}/capacitador/empresas/${info.empresaId}/trabajadores`, payload, { headers });
        toast.success('Trabajador creado.');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
    setSaving(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6" onClick={e => e.stopPropagation()} data-testid="modal-trabajador">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">{isEdit ? 'Editar' : 'Nuevo'} trabajador</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 font-semibold">RUT *</label>
            <input value={form.rut} onChange={e => setForm({ ...form, rut: formatRutLive(e.target.value) })}
              disabled={isEdit}
              placeholder="12.345.678-9"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg disabled:bg-slate-50"
              data-testid="trab-input-rut" />
            {!isEdit && form.rut && !validateRut(form.rut) && (
              <p className="text-[10px] text-red-500 mt-1">RUT inválido</p>
            )}
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">Cargo</label>
            <input value={form.cargo} onChange={e => setForm({ ...form, cargo: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">Nombre *</label>
            <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg"
              data-testid="trab-input-nombre" />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">Apellido *</label>
            <input value={form.apellido} onChange={e => setForm({ ...form, apellido: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg"
              data-testid="trab-input-apellido" />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">Email *</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="nombre@empresa.cl"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg"
              data-testid="trab-input-email" />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">Teléfono</label>
            <input value={form.fono} onChange={e => setForm({ ...form, fono: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">Género</label>
            <select value={form.sexo} onChange={e => setForm({ ...form, sexo: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg">
              <option value="">-</option>
              <option value="MASCULINO">Masculino</option>
              <option value="FEMENINO">Femenino</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">Fecha de Nacimiento</label>
            <input type="date" value={form.fecha_nacimiento || ''} onChange={e => setForm({ ...form, fecha_nacimiento: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" />
          </div>
          <div className="col-span-2 flex justify-end gap-2 mt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">Cancelar</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold disabled:opacity-50"
              data-testid="trab-submit">
              {saving ? 'Guardando...' : (isEdit ? 'Guardar cambios' : 'Crear trabajador')}
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

function KpiCard({ icon: Icon, label, value, subtitle, color, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${color}15`, border: `1px solid ${color}30` }}>
          <Icon className="w-5 h-5" style={{ color }} strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
          <p className="text-2xl font-black text-slate-900 leading-tight">{value}</p>
          <p className="text-[10px] text-slate-500 truncate">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}
